import {
  IsArray,
  IsDate,
  IsEnum,
  IsNumber,
  IsOptional,
  IsString
} from 'class-validator';
import { IStatusHistory } from './common.dto';
import { ICustomerUser } from './customer.dto';
import {
  IProductRequirement,
  IProductSubscriptionPhase,
  IProductV2
} from './productV2.dto';
import { Types } from 'mongoose';
import type {
  PaymentProcessorId,
  PaymentProcessorImplementationId
} from './payment/gateway';

export const SUBSCRIPTION_STATUS = {
  ACTIVE: 'active',
  PAUSED: 'paused',
  CANCELLED: 'cancelled',
  ENDED: 'ended'
} as const;

export type ISubscriptionStatus =
  (typeof SUBSCRIPTION_STATUS)[keyof typeof SUBSCRIPTION_STATUS];

export const SUBSCRIPTION_STATUSES = Object.values(SUBSCRIPTION_STATUS);

export class ISubscriptionNextCycleOverride {
  requirements?: IProductRequirement[];
}

export interface ISubscriptionExtras {
  /**
   * Subscription action flags, resolved at read time in precedence
   * order product → category → org (each level's `subscription` config).
   * Not persisted on the subscription document. Default false (action shown).
   */
  blockSkipRenewal?: boolean;
  blockRenewNow?: boolean;
  blockReschedule?: boolean;
  blockCancel?: boolean;
  blockResume?: boolean;
  /**
   * Any other extras that may be needed for the subscription.
   */
  [key: string]: any;
}

/**
 * Processor references stamped at subscription creation so renewals keep
 * charging the original vault after an org switches payment gateways.
 */
export interface ISubscriptionPayment {
  /** The vendor holding the money. Never `legacy` — that is a routing mode. */
  gateway: PaymentProcessorId;
  /**
   * The concrete code path that ran, which `gateway` cannot express: modern
   * Stripe and the frozen legacy Stripe path are both `gateway: 'stripe'` and
   * produce the same `pi_…` ids, so this is the only way to tell them apart
   * after the fact.
   */
  processor?: PaymentProcessorImplementationId;
  processorCustomerId?: string;
  /**
   * Saved payment method to charge on renew (`payt_…` for Whop, `pm_…` for
   * Stripe when stamped). When absent, renewals fall back to the vault /
   * member's default or first listed method.
   */
  paymentMethodId?: string;
}

export class ISubscription {
  _id: string;
  organization: string;
  customer: string | ICustomerUser;
  product: string | IProductV2;
  currentCycle: number;
  nextCycleDate: Date;
  upcomingReminderDate?: Date;
  status: ISubscriptionStatus;
  statusHistory: IStatusHistory<ISubscriptionStatus>[];
  renewalHistory: RenewalHistory[];
  phases: IProductSubscriptionPhase[];
  extras: ISubscriptionExtras;
  meta: any;
  /** Payment processor / vault stamped at create time. */
  payment?: ISubscriptionPayment;
  createdAt?: Date;
  updatedAt?: Date;
  productOptions?: { [type: string]: string };
  nextCycleOverrides?: ISubscriptionNextCycleOverride;
  couponCode?: string;
  /** Promotion IDs carried from checkout for cycle-limited subscription discounts. */
  promotionIds?: string[];
}

export class SubscriptionStatusHistory {
  prevStatus: ISubscriptionStatus;
  newStatus: ISubscriptionStatus;
  updatedAt: Date;
  userId: string;
}

export enum RENEWAL_HISTORY_TYPES {
  renewed = 'renewed',
  update_renewal_date = 'update_renewal_date',
  skip_cycle = 'skip_cycle'
}

Object.freeze(RENEWAL_HISTORY_TYPES);

export class RenewalHistory {
  cycleNumber: number;
  updatedAt: Date;
  userId: string;
  type: RENEWAL_HISTORY_TYPES;
  previousRenewalDate?: Date;
  newRenewalDate?: Date;
}

export class CreateSubscriptionDTO {
  currency: 'USD' | 'CAD' | 'usd' | 'cad';
  customer: string;
  product: string;
  nextCycleDate: Date;
  extras?: any;
  meta?: any;
  payment?: ISubscriptionPayment;
  phases?: IProductSubscriptionPhase[];
  productOptions?: { [type: string]: string };
  currentCycle?: number;
  couponCode?: string;
  promotionIds?: string[];
}

export class UpdateSubscriptionDTO {
  @IsOptional()
  @IsArray()
  phases?: IProductSubscriptionPhase[];

  @IsDate()
  @IsOptional()
  nextCycleDate?: Date;

  @IsOptional()
  @IsEnum(['active', 'paused', 'cancelled'])
  status?: ISubscriptionStatus;

  @IsOptional()
  @IsNumber()
  currentCycle?: number;

  @IsOptional()
  @IsString()
  couponCode?: string;

  @IsOptional()
  @IsArray()
  @IsString({ each: true })
  promotionIds?: string[];
}

export class ExportSubscriptionsQuery {
  dateFrom?: string;
  dateTo?: string;
}

export class GetSubscriptionDTO {
  @IsString()
  @IsOptional()
  _id?: string;

  @IsString()
  @IsOptional()
  subscriptionId?: string;

  @IsString()
  @IsOptional()
  customer?: Types.ObjectId;

  @IsString()
  @IsOptional()
  product?: Types.ObjectId;

  @IsString()
  @IsOptional()
  status?: ISubscriptionStatus;

  @IsNumber()
  @IsOptional()
  currentCycle?: number;

  @IsDate()
  @IsOptional()
  nextCycleDate?: Date;

  @IsString()
  @IsOptional()
  customerName?: string;

  @IsString()
  @IsOptional()
  productName?: string;

  @IsDate()
  @IsOptional()
  createdAfter?: Date;

  @IsDate()
  @IsOptional()
  createdBefore?: Date;

  @IsDate()
  @IsOptional()
  nextCycleDateAfter?: Date;

  @IsDate()
  @IsOptional()
  nextCycleDateBefore?: Date;

  @IsString()
  @IsOptional()
  sortKey?: string;

  @IsString()
  @IsOptional()
  sortOrder?: string;

  @IsNumber()
  @IsOptional()
  limit?: number;

  @IsNumber()
  @IsOptional()
  offset?: number;

  /** Product category ID – subscriptions whose product has this category */
  @IsString()
  @IsOptional()
  productCategory?: string;
}
